import { getLocalDateString } from "../helpers/getLocalDateString";

// Tracks the rep's "recording for today" pointer in localStorage:
//  - recordingId: the id we resume into instead of starting a new recording
//  - localDate: today's date (device-local) the recordingId was created for;
//    a stored session from a previous calendar day is treated as expired
//  - nextChunkIndex: the next chunk index to use, persisted synchronously
//    on every enqueue so it survives reloads/crashes without depending on
//    IndexedDB. See useRetailRecording's +10 resume buffer for why a
//    slightly-stale value here is safe to work around.
const SESSION_STORAGE_KEY = "retail-recording-session";

type RetailRecordingSession = {
  recordingId: string;
  localDate: string;
  nextChunkIndex: number;
};

const readSession = (): RetailRecordingSession | null => {
  const raw = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<RetailRecordingSession>;
    if (
      typeof parsed.recordingId !== "string" ||
      typeof parsed.localDate !== "string"
    ) {
      return null;
    }

    return {
      recordingId: parsed.recordingId,
      localDate: parsed.localDate,
      nextChunkIndex:
        typeof parsed.nextChunkIndex === "number" ? parsed.nextChunkIndex : 0,
    };
  } catch {
    return null;
  }
};

const writeSession = (session: RetailRecordingSession): void => {
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
};

const saveSession = (
  session: Omit<RetailRecordingSession, "localDate"> & { localDate?: string }
): void => {
  writeSession({
    recordingId: session.recordingId,
    localDate: session.localDate ?? getLocalDateString(),
    nextChunkIndex: session.nextChunkIndex,
  });
};

// Returns the session only if it was created today; a session from a
// previous day is considered stale (the rep needs a fresh recording today).
const getActiveSessionForToday = (): RetailRecordingSession | null => {
  const session = readSession();
  if (!session) {
    return null;
  }

  return session.localDate === getLocalDateString() ? session : null;
};

const updateNextChunkIndex = (
  recordingId: string,
  nextChunkIndex: number
): void => {
  const session = readSession();
  if (!session || session.recordingId !== recordingId) {
    return;
  }

  writeSession({ ...session, nextChunkIndex });
};

const clearSession = (): void => {
  localStorage.removeItem(SESSION_STORAGE_KEY);
};

export {
  clearSession,
  getActiveSessionForToday,
  saveSession,
  SESSION_STORAGE_KEY,
  updateNextChunkIndex,
};
export type { RetailRecordingSession };
